import { useMemo } from "react";

/**
 * ChatMessageBody — renders a chat message's text with inline media
 * previews. Image / GIF / video URLs render below the text as embeds;
 * regular URLs become clickable links. Everything runs client-side —
 * no third-party unfurl service in the loop.
 *
 * Regexes deliberately kept narrow. Anything unrecognised falls back to
 * plain text so a hostile URL can't smuggle in an <iframe> or <script>.
 */
const URL_RE = /(https?:\/\/[^\s<>"]+)/g;

const IMAGE_EXT_RE = /\.(png|jpe?g|gif|webp|avif|bmp|svg)(\?.*)?$/i;
const VIDEO_EXT_RE = /\.(mp4|webm|mov|m4v)(\?.*)?$/i;

// YouTube — matches both `youtu.be/xxx` and `youtube.com/watch?v=xxx`.
const YT_RE = /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|shorts\/))([A-Za-z0-9_-]{6,20})/;

// Native reels — matches /reels/:id or /r/:id on our own domain.
const REELS_RE = /\/(?:reels|r)\/([A-Za-z0-9_-]{6,64})(?:[/?#]|$)/;

// Community invite links — auto-preview so it feels like a real invite card.
const INVITE_RE = /\/c\/([A-Za-z0-9-]{6,64})(?:\?[^\s]*invite=([A-Za-z0-9_-]{6,20}))?/;

function classify(url) {
  try {
    const u = new URL(url);
    if (IMAGE_EXT_RE.test(u.pathname)) return { kind: "image", url };
    if (VIDEO_EXT_RE.test(u.pathname)) return { kind: "video", url };
    const yt = url.match(YT_RE);
    if (yt) return { kind: "youtube", id: yt[1] };
    // Only treat as an in-app reel/invite when the host matches the current
    // origin — cross-origin lookalikes shouldn't fake a native embed.
    if (typeof window !== "undefined" && u.origin === window.location.origin) {
      const reel = u.pathname.match(REELS_RE);
      if (reel) return { kind: "reel", id: reel[1], url };
      const inv = url.match(INVITE_RE);
      if (inv) return { kind: "invite", communityId: inv[1], code: inv[2], url };
    }
    return { kind: "link", url };
  } catch {
    return null;
  }
}

export default function ChatMessageBody({ text }) {
  const parts = useMemo(() => {
    if (!text) return [];
    const out = [];
    let lastIndex = 0;
    const matches = [...text.matchAll(URL_RE)];
    matches.forEach((m) => {
      const idx = m.index ?? 0;
      if (idx > lastIndex) out.push({ type: "text", value: text.slice(lastIndex, idx) });
      out.push({ type: "url", value: m[0] });
      lastIndex = idx + m[0].length;
    });
    if (lastIndex < text.length) {
      out.push({ type: "text", value: text.slice(lastIndex) });
    }
    return out;
  }, [text]);

  const embeds = useMemo(() => {
    const seen = new Set();
    const found = [];
    for (const p of parts) {
      if (p.type !== "url") continue;
      const c = classify(p.value);
      if (!c) continue;
      const key = c.kind + (c.id || c.url || "");
      if (seen.has(key)) continue;
      seen.add(key);
      found.push(c);
    }
    return found.slice(0, 3); // never more than 3 embeds per message
  }, [parts]);

  return (
    <div className="space-y-2">
      <div className="text-sm text-zinc-200 whitespace-pre-wrap leading-relaxed break-words">
        {parts.map((p, i) =>
          p.type === "url" ? (
            <a
              key={`url-${i}-${p.value.slice(0, 32)}`}
              href={p.value}
              target="_blank"
              rel="noopener noreferrer"
              className="text-rose-300 hover:text-rose-200 underline underline-offset-2 break-all"
            >
              {p.value}
            </a>
          ) : (
            <span key={`txt-${i}-${p.value.slice(0, 32)}`}>{p.value}</span>
          ),
        )}
      </div>
      {embeds.length > 0 && (
        <div className="space-y-2" data-testid="chat-embeds">
          {embeds.map((e, i) => (
            <ChatEmbed key={e.url || `embed-${i}`} embed={e} />
          ))}
        </div>
      )}
    </div>
  );
}

function ChatEmbed({ embed }) {
  if (embed.kind === "image") {
    return (
      <a
        href={embed.url}
        target="_blank"
        rel="noopener noreferrer"
        className="block max-w-sm rounded-md overflow-hidden border border-white/10 bg-black/40"
        data-testid="chat-embed-image"
      >
        <img
          src={embed.url}
          alt="attachment"
          loading="lazy"
          className="w-full h-auto max-h-80 object-cover"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      </a>
    );
  }
  if (embed.kind === "video") {
    return (
      <video
        src={embed.url}
        controls
        preload="metadata"
        className="max-w-sm rounded-md border border-white/10 bg-black/40"
        data-testid="chat-embed-video"
      />
    );
  }
  if (embed.kind === "youtube") {
    return (
      <div className="max-w-sm rounded-md overflow-hidden border border-white/10 bg-black" data-testid="chat-embed-youtube">
        <div className="aspect-video">
          <iframe
            title="YouTube preview"
            src={`https://www.youtube.com/embed/${embed.id}`}
            className="w-full h-full"
            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </div>
    );
  }
  if (embed.kind === "reel") {
    return (
      <a
        href={embed.url}
        className="block max-w-xs bg-gradient-to-br from-rose-900/40 to-black border border-rose-500/30 rounded-md px-4 py-3 hover:border-rose-500/60 transition-colors"
        data-testid="chat-embed-reel"
      >
        <div className="text-[10px] uppercase tracking-widest text-rose-400">Panda Reel</div>
        <div className="text-sm text-white mt-1">Tap to watch</div>
      </a>
    );
  }
  if (embed.kind === "invite") {
    return (
      <a
        href={embed.url}
        className="block max-w-xs bg-gradient-to-br from-amber-500/15 to-rose-500/10 border border-amber-400/40 rounded-md px-4 py-3 hover:border-amber-400/70 transition-colors"
        data-testid="chat-embed-invite"
      >
        <div className="text-[10px] uppercase tracking-widest text-amber-300">Community invite</div>
        <div className="text-sm text-white mt-1">One-tap join</div>
      </a>
    );
  }
  return null;
}
